import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/Card.jsx';
import { superAdminApi } from '../../api/superAdmin.js';
import { resolveFlowMode } from '../../api/ivrAgents.js';
import { useSSEChat } from '../../hooks/useSSEChat.js';
import { ChatPane } from '../../components/IvrAgents/Workspace/ChatPane.jsx';
import { ActionProposalContext } from '../../components/SuperAdmin/ActionProposalCard.jsx';
import { ArrowLeft, RefreshCw, Phone, Bot, Building2, Globe2, CircleCheck, CircleAlert, Copy, Check, Loader2, MessageSquarePlus, Cable, Code2 } from 'lucide-react';

/**
 * /admin/tenants/:hostId — Super Admin tenant 運維工作區
 *   左：tenant 即時摘要（PBX / 分機註冊 / AI 角色清單）
 *   右：AI 運維對話（Opus 4.8 1M；ivr-builder /api/admin/tenants/:hostId/chat）
 *       寫入動作 → AI 出 action-proposal 卡 → 按確認 → 執行 → 結果回灌對話
 */
export default function TenantOpsPage() {
    const { hostId } = useParams();
    const { t } = useTranslation('superAdmin');

    // ── 左欄資料 ──
    const [tenant, setTenant] = useState(null);
    const [regs, setRegs] = useState(null);
    const [avatars, setAvatars] = useState([]);
    const [seatInfo, setSeatInfo] = useState(null);   // { seats, seatsUsed } — 擁有帳號的席次池
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        Promise.allSettled([
            superAdminApi.tenant(hostId),
            superAdminApi.registrations(hostId),
            superAdminApi.tenantAvatars(hostId),
        ]).then(([tr, rr, ar]) => {
            const tn = tr.status === 'fulfilled' ? (tr.value.tenant || tr.value) : null;
            if (tn) setTenant(tn);
            if (rr.status === 'fulfilled') setRegs(rr.value);
            if (ar.status === 'fulfilled') setAvatars(ar.value.avatars || []);
            // 席次（最大數/類型/用量）— 從 tenant 擁有帳號查雙池
            if (tn?.ownerAccountUid) {
                superAdminApi.seats(tn.ownerAccountUid).then(setSeatInfo).catch(() => setSeatInfo(null));
            }
        }).finally(() => setLoading(false));
    }, [hostId]);
    useEffect(load, [load]);

    // ── 右欄對話（session = sa-<hostId>，歷史/重連/停止共用既有端點）──
    const chat = useSSEChat(`sa-${hostId}`, {
        sendUrl: `/ivr-builder/api/admin/tenants/${encodeURIComponent(hostId)}/chat`,
    });

    // 提案卡片 context：http 類執行完把結果回灌對話；skill 類由卡片直接 sendMessage
    const proposalCtx = useMemo(() => ({
        sendMessage: chat.sendMessage,
        onExecuted: (proposal, result) => {
            const ok = !result?.error;
            const summary = JSON.stringify(result || {}).slice(0, 800);
            chat.sendMessage(
                `[action-confirmed] ${proposal.action}「${proposal.title}」已由超管按下確認並執行，結果：${ok ? '成功' : '失敗'}\n${summary}\n請確認結果並用白話總結現在的狀態。`,
                { displayText: `${ok ? '✅' : '❌'} ${t('proposal.executedFeed', { title: proposal.title })}` }
            );
            load();   // 設定可能變了 → 左欄刷新
        },
    }), [chat.sendMessage, load, t]);

    // 開新對話：清空 sa-<hostId> 訊息 + 斷開 claude session → 下一句話 = skills 全新開始
    const [resetting, setResetting] = useState(false);
    const [resetConfirm, setResetConfirm] = useState(false);
    const resetChat = async () => {
        if (!resetConfirm) {
            setResetConfirm(true);
            setTimeout(() => setResetConfirm(false), 4000);
            return;
        }
        setResetConfirm(false);
        setResetting(true);
        try {
            await superAdminApi.resetChat(hostId);
            await chat.reload();
        } catch (e) {
            alert(t('ops.newChatFailed', { reason: e.message }));
        } finally {
            setResetting(false);
        }
    };

    // ── SIP Trunk 自助切換：特例開通給該租戶的商用帳號 ──
    // 預設商用帳號唯讀（切錯代價高：conf 重產、進線對應換一套、席次計費基礎改變）；
    // 客戶 PBX 不出 internet 只能走 trunk、又自己有 IT 時，超管在這裡開通。
    const [trunkGrantBusy, setTrunkGrantBusy] = useState(false);
    const toggleTrunkSelfService = async () => {
        const next = !(tenant?.trunkSelfServiceEnabled === true);
        if (next && !window.confirm(t('trunkGrant.confirmOn', { name: tenant?.displayName || hostId }))) return;
        if (!next && !window.confirm(t('trunkGrant.confirmOff', { name: tenant?.displayName || hostId }))) return;
        setTrunkGrantBusy(true);
        try {
            const r = await superAdminApi.setTrunkSelfService(hostId, next);
            setTenant((cur) => ({ ...(cur || {}), ...(r.tenant || { trunkSelfServiceEnabled: next }) }));
        } catch (e) {
            alert(t('trunkGrant.failed', { reason: e.message }));
        } finally {
            setTrunkGrantBusy(false);
        }
    };

    // ── API 開關（商用帳號）：A = API 文件頁、B = 角色管理 API；B 要 A 開著才能開 ──
    // 經銷商 / 超管不受影響（一律看得到）；第一階段只管後台畫面，API key 本身尚未依此擋（見後端註解）
    const [apiBusy, setApiBusy] = useState(null);   // 'docs' | 'avatar' | null
    const apiDocsOn = tenant?.apiDocsEnabled === true;
    const avatarApiOn = apiDocsOn && tenant?.avatarApiEnabled === true;
    const toggleApi = async (which) => {
        const patch = which === 'docs' ? { apiDocsEnabled: !apiDocsOn } : { avatarApiEnabled: !avatarApiOn };
        setApiBusy(which);
        try {
            const r = await superAdminApi.setApiAccess(hostId, patch);
            setTenant((cur) => ({ ...(cur || {}), ...(r.tenant || patch) }));
        } catch (e) {
            alert(t('apiAccess.failed', { reason: e.message }));
        } finally {
            setApiBusy(null);
        }
    };

    // 角色編號複製（貼進對話「幫我看 <id> 這個角色…」用）
    const [copiedId, setCopiedId] = useState(null);
    const copyId = (id) => {
        navigator.clipboard?.writeText(id).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
        });
    };

    const regList = regs?.registrations || [];
    const regOk = Array.isArray(regList) ? regList.filter((r) => /Registered/i.test(r.status || r.state || '')).length : 0;
    const pbx = tenant?.pbx || {};

    return (
        <div className="flex gap-6 h-[calc(100dvh-160px)] min-h-[480px]">
            {/* ── 左欄：tenant 摘要 ── */}
            <div className="w-[340px] shrink-0 overflow-y-auto space-y-4 pr-1">
                <div className="flex items-center gap-2">
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-accent-700 transition">
                        <ArrowLeft size={13} /> {t('ops.back')}
                    </Link>
                    <Link to={`/admin/tenants/${hostId}/calls`} className="inline-flex items-center gap-1 text-xs text-accent-700 hover:underline">
                        <Phone size={12} /> {t('ops.viewCalls')}
                    </Link>
                    <button onClick={load} className="ml-auto inline-flex items-center gap-1 text-xs text-ink-500 hover:text-accent-700 transition">
                        <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> {t('overview.refresh')}
                    </button>
                </div>

                <Card padded={false}>
                    <div className="p-4">
                        <div className="flex items-center gap-2">
                            <span className={`size-2 rounded-full ${tenant?.enabled !== false ? 'bg-success-500' : 'bg-ink-300'}`} />
                            <span className="heading-display text-lg font-semibold text-ink-900">{tenant?.displayName || hostId}</span>
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-cream-100 text-ink-500">{hostId}</span>
                        </div>
                        <div className="mt-3 space-y-1.5 text-xs text-ink-600">
                            <div className="flex items-center gap-1.5"><Building2 size={12} className="text-ink-400" /> {pbx.model || (pbx.publicIp ? 'PBX' : t('ops.noPbx'))} {pbx.publicIp && `· ${pbx.publicIp}:${pbx.ipPhoneRegisterPort || pbx.publicSipPort || ''}`}</div>
                            {tenant?.region && <div className="flex items-center gap-1.5"><Globe2 size={12} className="text-ink-400" /> region: {tenant.region}</div>}
                            <div className="flex items-center gap-1.5">
                                {regOk === (tenant?.ipPhones || []).length && regOk > 0
                                    ? <CircleCheck size={12} className="text-success-500" />
                                    : <CircleAlert size={12} className="text-warn-500" />}
                                {t('ops.regStatus', { ok: regOk, total: (tenant?.ipPhones || []).length })}
                            </div>
                        </div>
                    </div>
                </Card>

                {/* 東訊專屬：大盤商總覽入口（只在 tecom 租戶顯示） */}
                {hostId === 'tecom' && (
                    <Link to="/sdx500-distributors" className="block">
                        <Card padded={false} className="hover:border-accent-400 transition">
                            <div className="p-4 flex items-center gap-3">
                                <span className="size-9 rounded-lg bg-accent-50 text-accent-700 grid place-items-center shrink-0"><Building2 size={17} /></span>
                                <div className="min-w-0">
                                    <div className="text-[13px] font-medium text-ink-900">東訊大盤商</div>
                                    <div className="text-[11.5px] text-ink-400">各大盤商 + 底下客戶 / 授權數</div>
                                </div>
                                <span className="ml-auto text-ink-300 text-lg">›</span>
                            </div>
                        </Card>
                    </Link>
                )}

                {/* 席次：帳號雙池（AA 總機 / CS 客服）用量 vs 上限；經銷商=不限 */}
                {seatInfo && (
                    <Card padded={false}>
                        <div className="p-4">
                            <div className="text-xs font-mono uppercase tracking-[0.2em] text-ink-400 mb-2">{t('ops.seats')}</div>
                            <div className="space-y-1.5">
                                {[['plan_01', 'AA', 'bg-[#dce8f6] text-[#3f6592]'], ['plan_02', 'CS', 'bg-[#fdf3c8] text-[#8a6d1f]']].map(([key, label, chip]) => {
                                    const limit = seatInfo.seats?.[key];
                                    const used = seatInfo.seatsUsed?.[key] ?? 0;
                                    return (
                                        <div key={key} className="flex items-center gap-2 text-xs">
                                            <span className={`font-mono text-[9px] px-1 py-0.5 rounded ${chip}`}>{label}</span>
                                            <span className="text-ink-500">{key === 'plan_01' ? t('ops.seatAA') : t('ops.seatCS')}</span>
                                            <span className="ml-auto tnum font-semibold text-ink-900">
                                                {used} / {typeof limit === 'number' ? limit : t('ops.seatUnlimited')}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Card>
                )}

                {/* SIP Trunk 自助切換開通（super_admin 專屬；/admin 整區已由 RequireSuperAdmin 守住）*/}
                <Card padded={false}>
                    <div className="p-4">
                        <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-ink-400 mb-2">
                            <Cable size={12} /> {t('trunkGrant.title')}
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="text-[13px] text-ink-900">
                                    {tenant?.trunkSelfServiceEnabled === true ? t('trunkGrant.on') : t('trunkGrant.off')}
                                </div>
                                <p className="text-[11.5px] text-ink-400 mt-0.5">
                                    {tenant?.trunkSelfServiceEnabled === true ? t('trunkGrant.onHint') : t('trunkGrant.offHint')}
                                </p>
                                {tenant?.trunkSelfServiceEnabled === true && tenant?.trunkSelfServiceGrantedBy && (
                                    <p className="text-[11px] text-ink-400 mt-1">
                                        {t('trunkGrant.grantedBy', {
                                            who: tenant.trunkSelfServiceGrantedBy,
                                            when: tenant.trunkSelfServiceGrantedAt
                                                ? new Date(tenant.trunkSelfServiceGrantedAt).toLocaleString()
                                                : '—',
                                        })}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={toggleTrunkSelfService}
                                disabled={trunkGrantBusy || !tenant}
                                className={`shrink-0 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors
                                    focus-visible:outline-2 focus-visible:outline-accent-500 disabled:opacity-40 disabled:cursor-not-allowed
                                    ${tenant?.trunkSelfServiceEnabled === true
                                        ? 'border-cream-300 text-ink-600 hover:bg-cream-100'
                                        : 'border-accent-400 text-accent-700 hover:bg-accent-50'}`}
                            >
                                {trunkGrantBusy && <Loader2 size={12} className="animate-spin" />}
                                {tenant?.trunkSelfServiceEnabled === true ? t('trunkGrant.revoke') : t('trunkGrant.grant')}
                            </button>
                        </div>
                    </div>
                </Card>

                {/* 商用帳號 API 開關（super_admin 專屬；經銷商一律開，不顯示）*/}
                {seatInfo?.accountRole === 'business' && <Card padded={false}>
                    <div className="p-4">
                        <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-ink-400 mb-2">
                            <Code2 size={12} /> {t('apiAccess.title')}
                        </div>
                        {/* 角色管理只在 API 文件頁開了之後才出現（關掉文件頁時設定值保留，再開時恢復顯示）*/}
                        {[
                            { key: 'docs', on: apiDocsOn, label: t('apiAccess.docs'), disabled: false },
                            ...(apiDocsOn ? [{ key: 'avatar', on: avatarApiOn, label: t('apiAccess.avatar'), disabled: false }] : []),
                        ].map((it) => (
                            <div key={it.key} className="flex items-center gap-3 py-1">
                                <span className={`min-w-0 flex-1 text-[13px] ${it.disabled ? 'text-ink-400' : 'text-ink-900'}`}>{it.label}</span>
                                {/* 載入圈放開關左邊、固定佔位 → 轉圈時開關不會位移 */}
                                <span className="shrink-0 inline-flex w-3 justify-center" aria-hidden="true">
                                    {apiBusy === it.key && <Loader2 size={12} className="animate-spin text-ink-400" />}
                                </span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={it.on}
                                    aria-label={it.label}
                                    onClick={() => toggleApi(it.key)}
                                    disabled={!tenant || it.disabled || apiBusy !== null}
                                    className={`relative shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors
                                        focus-visible:outline-2 focus-visible:outline-accent-500 disabled:opacity-40 disabled:cursor-not-allowed
                                        ${it.on ? 'bg-accent-500' : 'bg-cream-300'}`}
                                >
                                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${it.on ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                </button>
                            </div>
                        ))}
                        <p className="text-[11.5px] text-ink-400 mt-1">{t('apiAccess.hint')}</p>
                    </div>
                </Card>}

                <Card padded={false}>
                    <div className="p-4">
                        <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-ink-400 mb-2"><Phone size={12} /> {t('ops.extensions')}</div>
                        <div className="flex flex-wrap gap-1.5">
                            {(tenant?.ipPhones || []).map((p) => {
                                const reg = regList.find?.((r) => String(r.ext || r.endpoint || '').includes(p.ext));
                                const ok = reg && /Registered/i.test(reg.status || reg.state || '');
                                return (
                                    <span key={p.ext} title={p.seatType || ''} className={`font-mono text-[11px] px-1.5 py-0.5 rounded ${ok ? 'bg-success-100 text-success-500' : 'bg-cream-100 text-ink-400'}`}>
                                        {p.ext}
                                    </span>
                                );
                            })}
                            {(tenant?.ipPhones || []).length === 0 && <span className="text-xs text-ink-400 italic">{t('ops.noExts')}</span>}
                        </div>
                    </div>
                </Card>

                <Card padded={false}>
                    <div className="p-4">
                        <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-ink-400 mb-2"><Bot size={12} /> {t('ops.avatars', { n: avatars.length })}</div>
                        {(() => {
                            // 還在抓角色清單 → 顯示 loading（不然會先閃「沒有角色」誤導）
                            if (loading && avatars.length === 0) {
                                return (
                                    <div className="flex items-center gap-2 text-xs text-ink-400 py-1">
                                        <Loader2 size={12} className="animate-spin" /> {t('ops.loadingAvatars')}
                                    </div>
                                );
                            }
                            // 分「啟用 / 非啟用」兩組；列＝名稱＋可複製編號（貼進對話請 AI 診斷）
                            const enabledList = avatars.filter((a) => a.enabled === true);
                            const disabledList = avatars.filter((a) => a.enabled !== true);
                            // 角色類型：問卷(survey) / AA 總機(transfer-only) / CS 客服(full) — 同 AvatarCard 判定
                            // 用共用 resolveFlowMode（看 flow 實際節點）——不可只讀 flowMode 欄位，
                            // 否則超管看到的型別會跟客戶後台/配額計數分岔（0813 東訊 tecom）。
                            const typeOf = (a) => {
                                if (a.newMode === 'survey' || a.surveyMode === true) return 'survey';
                                const fm = resolveFlowMode(a);
                                if (fm === 'survey') return 'survey';
                                return fm === 'transfer-only' ? 'aa' : 'cs';
                            };
                            const TYPE_STYLE = {
                                aa: 'bg-[#dce8f6] text-[#3f6592]',          // 藍（總機）
                                cs: 'bg-[#fdf3c8] text-[#8a6d1f]',          // 黃（客服）
                                survey: 'bg-[#e6f3eb] text-[#4c7a5c]',      // 綠（問卷）
                            };
                            const Row = ({ a, muted }) => {
                                const id = a.avatarId || a.id;
                                const type = typeOf(a);
                                return (
                                    <li className="text-xs flex items-center gap-1.5">
                                        <span className={`size-1.5 rounded-full shrink-0 ${muted ? 'bg-ink-300' : 'bg-success-500'}`} />
                                        <span className={`font-medium truncate ${muted ? 'text-ink-400' : 'text-ink-900'}`}>{a.name || '(未命名)'}</span>
                                        <span className={`shrink-0 font-mono text-[9px] uppercase tracking-[0.1em] px-1 py-0.5 rounded ${muted ? 'bg-cream-100 text-ink-300' : TYPE_STYLE[type]}`}>
                                            {type === 'survey' ? t('ops.typeSurvey') : type.toUpperCase()}
                                        </span>
                                        <button
                                            onClick={() => copyId(id)}
                                            title={`${t('ops.copyId')}\n${id}`}
                                            className="ml-auto shrink-0 inline-flex items-center gap-1 font-mono text-[10px] text-ink-400 px-1 py-0.5 rounded hover:bg-cream-100 hover:text-accent-700 transition focus-visible:outline-2 focus-visible:outline-accent-500"
                                        >
                                            {copiedId === id
                                                ? <><Check size={10} className="text-success-500" />{t('ops.copied')}</>
                                                : <><Copy size={10} />{id.slice(0, 8)}…</>}
                                        </button>
                                    </li>
                                );
                            };
                            return (
                                <>
                                    {enabledList.length > 0 && (
                                        <>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-success-500 mb-1.5">{t('ops.enabledGroup')} · {enabledList.length}</div>
                                            <ul className="space-y-1 mb-3">{enabledList.map((a) => <Row key={a.avatarId || a.id} a={a} />)}</ul>
                                        </>
                                    )}
                                    {disabledList.length > 0 && (
                                        <>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1.5">{t('ops.disabledGroup')} · {disabledList.length}</div>
                                            <ul className="space-y-1">{disabledList.map((a) => <Row key={a.avatarId || a.id} a={a} muted />)}</ul>
                                        </>
                                    )}
                                    {avatars.length === 0 && <div className="text-xs text-ink-400 italic">{t('ops.noAvatars')}</div>}
                                </>
                            );
                        })()}
                    </div>
                </Card>
            </div>

            {/* ── 右欄：AI 運維對話 ── */}
            <div className="flex-1 min-w-0 flex flex-col rounded-lg border border-cream-200 bg-cream-50/40 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-cream-200 flex items-center gap-2">
                    <Bot size={15} className="text-accent-500" />
                    <span className="text-sm font-semibold text-ink-900">{t('ops.chatTitle')}</span>
                    <button
                        type="button"
                        onClick={resetChat}
                        disabled={resetting || chat.isStreaming}
                        title={t('ops.newChatHint')}
                        className={`ml-auto inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors
                            focus-visible:outline-2 focus-visible:outline-accent-500 disabled:opacity-40 disabled:cursor-not-allowed
                            ${resetConfirm
                                ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                                : 'border-cream-300 text-ink-600 hover:bg-cream-100'}`}
                    >
                        {resetting ? <Loader2 size={13} className="animate-spin" /> : <MessageSquarePlus size={13} />}
                        {resetConfirm ? t('ops.newChatConfirm') : t('ops.newChat')}
                    </button>
                </div>
                <ActionProposalContext.Provider value={proposalCtx}>
                    <div className="flex-1 min-h-0">
                        <ChatPane chat={chat} avatarName={t('ops.chatTitle')} />
                    </div>
                </ActionProposalContext.Provider>
            </div>
        </div>
    );
}
